import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { type Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { identityApiRef, useApi } from '@backstage/frontend-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

const PAGE_SIZE = 25;
const SEARCH_DELAY_MS = 300;

const ALL_KINDS = ['Component', 'API', 'Resource', 'System', 'Domain', 'Group', 'Template'];

export const KIND_LABELS: Record<string, string> = {
  component: 'Onderdeel',
  api: 'API',
  resource: 'Bron',
  system: 'Systeem',
  domain: 'Domein',
  group: 'Groep',
  template: 'Template',
};

export const TYPE_LABELS: Record<string, string> = {
  'code-overheid': 'code.overheid.nl',
  'open-source': 'Open source',
  'json-schema': 'Gegevensschema',
  openapi: 'OpenAPI',
  organisatie: 'Organisatie',
  ministerie: 'Ministerie',
  dienst: 'Dienst',
  nieuw: 'Nieuwe repository',
  bestaand: 'Bestaande repository',
  techradar: 'Tech Radar',
  standaard: 'Standaard',
  artikel: 'Artikel',
  tool: 'Tool',
  tutorial: 'Tutorial',
  leidraad: 'Leidraad',
  community: 'Community',
  blog: 'Blog',
  website: 'Website',
  service: 'Dienst',
  team: 'Team',
};

export const label = (labels: Record<string, string>, value: string) =>
  labels[value.toLowerCase()] ?? labels[value] ?? value;

type Facet = { value: string; count: number };

function FilterRow(props: { text: string; count?: number; checked: boolean; onToggle: () => void }) {
  const row = useRef<HTMLElement>(null);
  const { onToggle } = props;
  useEffect(() => {
    const element = row.current;
    element?.addEventListener('change', onToggle);
    return () => element?.removeEventListener('change', onToggle);
  }, [onToggle]);
  return (
    <nldd-list-item ref={row} checkbox="" checked={props.checked ? '' : undefined}>
      <nldd-cell>
        <nldd-checkbox decorative="" checked={props.checked ? '' : undefined} />
      </nldd-cell>
      <nldd-spacer-cell size="8" />
      <nldd-text-cell text={props.text} />
      {props.count === undefined ? null : (
        <>
          <nldd-spacer-cell size="8" />
          <nldd-text-cell
            width="fit-content"
            horizontal-alignment="right"
            color="secondary"
            text={props.count.toLocaleString('nl-NL')}
          />
        </>
      )}
    </nldd-list-item>
  );
}

export function FilterGroup(props: {
  title: string;
  facets: { value: string; count?: number }[];
  selected: string[];
  labels: Record<string, string>;
  onToggle: (value: string) => void;
}) {
  if (props.facets.length === 0) {
    return null;
  }
  return (
    <>
      <nldd-title size="5" text={props.title} heading-level="2" />
      <nldd-spacer size="8" />
      <nldd-list appearance="simple" dividers="never" accessible-label={props.title}>
        {props.facets.map(facet => (
          <FilterRow
            key={facet.value}
            text={label(props.labels, facet.value)}
            count={facet.count}
            checked={props.selected.includes(facet.value)}
            onToggle={() => props.onToggle(facet.value)}
          />
        ))}
      </nldd-list>
      <nldd-spacer size="24" />
    </>
  );
}

function FilterToken(props: { text: string; onDismiss: () => void }) {
  const token = useRef<HTMLElement>(null);
  const { onDismiss } = props;
  useEffect(() => {
    const element = token.current;
    element?.addEventListener('dismiss', onDismiss);
    return () => element?.removeEventListener('dismiss', onDismiss);
  }, [onDismiss]);
  return <nldd-token ref={token} text={props.text} control="dismiss" />;
}

export type EntityBrowserProps = {
  /** Left out under a head that already names the page. */
  title?: string;
  description?: string;
  /** Sits above the search field, for ways into this list that are not filters. */
  intro?: ReactNode;
  /** Limits the page to these kinds; with one kind the kind filter disappears. */
  kinds?: string[];
  itemName: string;
  /** Extra catalog filter for the whole page; pass a stable object. */
  filter?: Record<string, string | symbol | string[]>;
  href?: (entity: Entity) => string;
  badge?: (entity: Entity) => string | undefined;
};

const defaultHref = (entity: Entity) =>
  `/catalog/${entity.metadata.namespace ?? 'default'}/${entity.kind.toLowerCase()}/${entity.metadata.name}`;

export function EntityBrowser(props: EntityBrowserProps) {
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  const [params, setParams] = useSearchParams();
  const [ownership, setOwnership] = useState<string[]>([]);
  const [entities, setEntities] = useState<Entity[]>();
  const [owners, setOwners] = useState<Record<string, string>>({});
  const [total, setTotal] = useState(0);
  const [kindFacets, setKindFacets] = useState<Facet[]>([]);
  const [typeFacets, setTypeFacets] = useState<Facet[]>([]);
  const [failure, setFailure] = useState<string>();
  const search = useRef<HTMLElement>(null);
  const pagination = useRef<HTMLElement>(null);
  const openFilters = useRef<HTMLElement>(null);
  const openFiltersOverflow = useRef<HTMLElement>(null);
  const section = useRef<HTMLElement & { show?: () => void }>(null);

  const query = params.get('q') ?? '';
  const mine = params.get('van') === 'mij';
  const page = Number(params.get('pagina') ?? 1);
  const kinds = useMemo(() => params.getAll('soort'), [params]);
  const types = useMemo(() => params.getAll('type'), [params]);
  const allowedKinds = useMemo(() => props.kinds ?? ALL_KINDS, [props.kinds]);

  const update = useCallback(
    (change: (next: URLSearchParams) => void) => {
      setParams(current => {
        const next = new URLSearchParams(current);
        change(next);
        next.delete('pagina');
        return next;
      });
    },
    [setParams],
  );

  const toggle = useCallback(
    (name: string, value: string) =>
      update(next => {
        const values = next.getAll(name);
        next.delete(name);
        (values.includes(value) ? values.filter(item => item !== value) : [...values, value]).forEach(
          item => next.append(name, item),
        );
      }),
    [update],
  );

  useEffect(() => {
    identityApi.getBackstageIdentity().then(identity => setOwnership(identity.ownershipEntityRefs));
  }, [identityApi]);

  useEffect(() => {
    const element = search.current;
    let timer: ReturnType<typeof setTimeout>;
    const onInput = (event: Event) => {
      const value = (event as CustomEvent<{ value?: string }>).detail?.value ?? '';
      clearTimeout(timer);
      timer = setTimeout(
        () => update(next => (value ? next.set('q', value) : next.delete('q'))),
        SEARCH_DELAY_MS,
      );
    };
    element?.addEventListener('input', onInput);
    return () => {
      clearTimeout(timer);
      element?.removeEventListener('input', onInput);
    };
  }, [update]);

  useEffect(() => {
    const element = pagination.current;
    const onPage = (event: Event) => {
      const value = (event as CustomEvent<{ page: number }>).detail.page;
      setParams(current => {
        const next = new URLSearchParams(current);
        next.set('pagina', String(value));
        return next;
      });
    };
    element?.addEventListener('page-change', onPage);
    return () => element?.removeEventListener('page-change', onPage);
  });

  useEffect(() => {
    const button = openFilters.current;
    const item = openFiltersOverflow.current;
    const open = () => section.current?.show?.();
    button?.addEventListener('click', open);
    item?.addEventListener('select', open);
    return () => {
      button?.removeEventListener('click', open);
      item?.removeEventListener('select', open);
    };
  }, []);

  useEffect(() => {
    let current = true;
    const filter: Record<string, string | string[]> = {
      kind: kinds.length ? kinds : allowedKinds,
      ...props.filter,
      ...(types.length ? { 'spec.type': types } : {}),
      ...(mine ? { 'relations.ownedBy': ownership } : {}),
    };
    if (mine && ownership.length === 0) {
      return undefined;
    }
    (async () => {
      const [result, facets] = await Promise.all([
        catalogApi.queryEntities({
          filter,
          limit: PAGE_SIZE,
          offset: (page - 1) * PAGE_SIZE,
          orderFields: { field: 'metadata.title', order: 'asc' },
          ...(query
            ? { fullTextFilter: { term: query, fields: ['metadata.name', 'metadata.title', 'metadata.description'] } }
            : {}),
          fields: ['kind', 'metadata', 'spec.type', 'spec.owner'],
        } as Parameters<typeof catalogApi.queryEntities>[0]),
        catalogApi.getEntityFacets({
          filter: { kind: allowedKinds, ...props.filter, ...(mine ? { 'relations.ownedBy': ownership } : {}) },
          facets: ['kind', 'spec.type'],
        }),
      ]);
      const ownerRefs = [...new Set(result.items.map(item => String(item.spec?.owner ?? '')).filter(Boolean))];
      const found = ownerRefs.length ? await catalogApi.getEntitiesByRefs({ entityRefs: ownerRefs, fields: ['kind', 'metadata'] }) : { items: [] };
      if (!current) {
        return;
      }
      setOwners(
        Object.fromEntries(
          found.items.flatMap(owner =>
            owner ? [[stringifyEntityRef(owner), owner.metadata.title ?? owner.metadata.name]] : [],
          ),
        ),
      );
      setEntities(result.items);
      setTotal(result.totalItems);
      const byCount = (a: Facet, b: Facet) => b.count - a.count;
      setKindFacets([...(facets.facets.kind ?? [])].sort(byCount));
      setTypeFacets([...(facets.facets['spec.type'] ?? [])].sort(byCount).slice(0, 14));
      setFailure(undefined);
    })().catch(error => current && setFailure(error.message));
    return () => {
      current = false;
    };
  }, [allowedKinds, catalogApi, kinds, mine, ownership, page, props.filter, query, types]);

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const href = props.href ?? defaultHref;
  const tokens = [
    ...(mine ? [{ text: 'Van mijn team', remove: () => update(next => next.delete('van')) }] : []),
    ...kinds.map(value => ({ text: `Soort: ${label(KIND_LABELS, value)}`, remove: () => toggle('soort', value) })),
    ...types.map(value => ({ text: `Type: ${label(TYPE_LABELS, value)}`, remove: () => toggle('type', value) })),
  ];

  return (
    <div>
      <nldd-sidebar-section
        ref={section}
        sidebar-label="Filters"
        translations='{"components.sidebar-section.sheet-dismiss-action": "Klaar"}'
      >
        {props.title ? (
          <nldd-title slot="header" size="1" text={props.title} supporting-text={props.description} heading-level="1" />
        ) : null}

        <nldd-container slot="sidebar" padding="16" padding-bottom="8">
          <nldd-title size="5" text="Weergave" heading-level="2" />
          <nldd-spacer size="8" />
          <nldd-list appearance="simple" dividers="never" accessible-label="Weergave">
            <FilterRow
              text="Van mijn team"
              checked={mine}
              onToggle={() => update(next => (next.has('van') ? next.delete('van') : next.set('van', 'mij')))}
            />
          </nldd-list>
          <nldd-spacer size="24" />
          {allowedKinds.length > 1 ? (
            <FilterGroup
              title="Soort"
              facets={kindFacets}
              selected={kinds}
              labels={KIND_LABELS}
              onToggle={value => toggle('soort', value)}
            />
          ) : null}
          <FilterGroup
            title="Type"
            facets={typeFacets}
            selected={types}
            labels={TYPE_LABELS}
            onToggle={value => toggle('type', value)}
          />
        </nldd-container>

        {props.intro}

        <nldd-toolbar accessible-label="Zoeken en filteren">
          <nldd-toolbar-item slot="start" min-width="240px" width="100%" max-width="560px">
            <nldd-search-field ref={search} value={query} placeholder={`Zoek in ${props.itemName}`} />
            <nldd-menu-item slot="overflow" icon="search" text="Zoeken" />
          </nldd-toolbar-item>
          <nldd-toolbar-item class="portal-open-filters" slot="end">
            <nldd-button ref={openFilters} appearance="secondary" start-icon="filter" text="Filters" />
            <nldd-menu-item ref={openFiltersOverflow} slot="overflow" icon="filter" text="Filters" />
          </nldd-toolbar-item>
        </nldd-toolbar>

        {tokens.length ? (
          <>
            <nldd-spacer size="16" />
            <nldd-container layout="wrap" gap="8" vertical-alignment="center">
              {tokens.map(token => (
                <FilterToken key={token.text} text={token.text} onDismiss={token.remove} />
              ))}
            </nldd-container>
          </>
        ) : null}

        <nldd-spacer size="16" />
        <nldd-text size="sm" color="secondary" aria-live="polite">
          {entities ? `${total.toLocaleString('nl-NL')} ${props.itemName}` : 'Wordt opgehaald'}
        </nldd-text>
        <nldd-spacer size="4" />

        {failure ? (
          <nldd-banner variant="critical" text="De catalogus is nu niet beschikbaar" supporting-text={failure} />
        ) : (
          <nldd-list id="resultaten" accessible-label={props.title ?? props.itemName}>
            <nldd-inline-dialog
              slot="no-results"
              icon="magnifier"
              text={`Geen ${props.itemName} gevonden`}
              supporting-text="Pas je zoekopdracht of filters aan."
            />
            {(entities ?? []).map(entity => {
              const owner = owners[String(entity.spec?.owner ?? '')];
              const type = entity.spec?.type ? label(TYPE_LABELS, String(entity.spec.type)) : '';
              const badge = props.badge?.(entity);
              return (
                <nldd-list-item key={entity.metadata.uid ?? entity.metadata.name} href={href(entity)}>
                  <nldd-text-cell
                    overline={[label(KIND_LABELS, entity.kind), type].filter(Boolean).join(' · ')}
                    text={entity.metadata.title ?? entity.metadata.name}
                    supporting-text={entity.metadata.description}
                  />
                  {badge ? (
                    <>
                      <nldd-spacer-cell size="8" hide-below="md" />
                      <nldd-cell hide-below="md">
                        <nldd-badge color="neutral" text={badge} />
                      </nldd-cell>
                    </>
                  ) : null}
                  {owner ? (
                    <>
                      <nldd-spacer-cell size="12" hide-below="md" />
                      <nldd-text-cell hide-below="md" width="fit-content" color="secondary" text={owner} />
                    </>
                  ) : null}
                  <nldd-spacer-cell size="12" />
                  <nldd-icon-cell size="20" color="secondary" icon="chevron-right" />
                </nldd-list-item>
              );
            })}
          </nldd-list>
        )}

        {pages > 1 ? (
          <>
            <nldd-spacer size="24" />
            <nldd-pagination ref={pagination} current={page} total={pages} centered="" />
          </>
        ) : null}
      </nldd-sidebar-section>
    </div>
  );
}
